// Phase G G5 — app-side LiveRoomService (the first real seam implementation).
//
// Thin I/O over the deployed edge functions via supabase.functions.invoke, which
// auto-attaches the cookie session JWT (and the platform anon key). No
// service_role, no inlined keys. All branching logic lives in the pure helpers
// (classifyFunctionError, makeIdentity) so the UI and tests don't touch network.

import type {
  AccessTokenResult,
  CreateRoomInput,
  EgressHandle,
  IngressHandle,
  IngressKind,
  LiveRoomService,
  RoomHandle,
  RoomRole,
} from '@archetelos/lib/live';

import { supabase } from '@/lib/supabase';

/** Typed reasons the UI branches on, mapped from edge-function failures. */
export type LiveErrorCode =
  | 'must_subscribe'
  | 'not_room_host'
  | 'not_approved_host'
  | 'room_not_found'
  | 'queue_disabled'
  | 'unauthenticated'
  | 'not_implemented'
  | 'unknown';

export class LiveServiceError extends Error {
  readonly code: LiveErrorCode;
  constructor(code: LiveErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'LiveServiceError';
    this.code = code;
  }
}

/**
 * Pure: map an edge-function HTTP failure (status + body `error` code) to a
 * typed UI reason. Named codes win over status so a 403 disambiguates between
 * "must subscribe", "not the host", and "not an approved host".
 */
export function classifyFunctionError(status: number, code?: string): LiveErrorCode {
  if (code === 'must_subscribe') return 'must_subscribe';
  if (code === 'not_room_host') return 'not_room_host';
  if (code === 'not_approved_host') return 'not_approved_host';
  if (code === 'room_not_found' || status === 404) return 'room_not_found';
  if (code === 'queue_disabled') return 'queue_disabled';
  if (status === 401) return 'unauthenticated';
  return 'unknown';
}

function defaultSuffix(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 6);
}

/**
 * Pure: a unique per-tab participant identity. LiveKit evicts an older session
 * that shares an identity, so two tabs (or a host self-preview + a viewer) must
 * differ. `rng` injectable for deterministic tests.
 */
export function makeIdentity(base: string, rng: () => string = defaultSuffix): string {
  return `${base}-${rng()}`;
}

async function readError(error: unknown): Promise<LiveServiceError> {
  // supabase-js FunctionsHttpError carries the raw Response on `.context`.
  const ctx = (error as { context?: unknown }).context;
  if (ctx instanceof Response) {
    let code: string | undefined;
    try {
      code = ((await ctx.json()) as { error?: string }).error;
    } catch {
      code = undefined;
    }
    return new LiveServiceError(classifyFunctionError(ctx.status, code));
  }
  const message = error instanceof Error ? error.message : String(error);
  return new LiveServiceError('unknown', message);
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw await readError(error);
  return data as T;
}

export const liveRoomService: LiveRoomService = {
  createRoom({ name, showId, simulcastTargets }: CreateRoomInput): Promise<RoomHandle> {
    return invoke<RoomHandle>('room-create', { name, showId, simulcastTargets });
  },

  createIngress(roomId: string, kind: IngressKind): Promise<IngressHandle> {
    return invoke<IngressHandle>('livekit-ingress', { roomId, kind });
  },

  async endRoom(roomId: string): Promise<void> {
    await invoke<unknown>('room-end', { roomId });
  },

  // Start the composite egress (recording + opt-in simulcast) at go-live. Server
  // is idempotent — safe to call on every host mount. roomId = livekit_room_name.
  async startEgress(roomId: string): Promise<void> {
    await invoke<unknown>('room-egress-start', { roomId });
  },

  // Role is server-derived in livekit-token; the seam's `role` param is ignored
  // here and the authoritative role comes back on the result. `name` (the PreJoin
  // entry) is the on-stream display name baked into the token.
  mintAccessToken(
    roomId: string,
    identity: string,
    _role: RoomRole,
    name?: string,
  ): Promise<AccessTokenResult> {
    return invoke<AccessTokenResult>('livekit-token', { room: roomId, identity, name });
  },

  startRecording(): Promise<EgressHandle> {
    throw new LiveServiceError('not_implemented', 'startRecording lands in G6');
  },

  // Guest queue (Inc 4a): returns the Stripe Checkout URL; the caller redirects.
  // The bid is recorded by the webhook on completion (queue-checkout writes
  // nothing). roomName = livekit_room_name.
  bidForGrid(roomName: string, amountCents: number): Promise<{ url: string }> {
    return invoke<{ url: string }>('queue-checkout', {
      room: roomName,
      amount_cents: amountCents,
    });
  },

  // Guest queue (Inc 4b): fired when an outbid grace hits zero; the server
  // reconciles and demotes precisely. roomId = rooms.id (uuid).
  async evaluateGrid(roomId: string): Promise<void> {
    await invoke<unknown>('evaluate-grid', { roomId });
  },
};
